import { brandAssets } from "../data/brandAssets";
import { projectWordings } from "../data/projectLanguage";
import { projectPassport } from "../data/projectPassport";
import { verifiedFacts } from "../data/verifiedFacts";
import type { SearchResult } from "../types";

function flattenPassport(): SearchResult[] {
  const namesText = [
    projectPassport.names.official,
    projectPassport.names.ru,
    projectPassport.names.uz,
    projectPassport.names.en,
    projectPassport.names.short,
    projectPassport.names.allowedMention,
    ...projectPassport.names.prohibitedVariants,
  ].join(" ");

  return [
    {
      id: "passport-name",
      group: "passport",
      title: "Официальное название",
      excerpt: projectPassport.names.official,
      meta: "Паспорт проекта",
      to: "/passport#name",
      searchableText: namesText,
    },
    {
      id: "passport-description",
      group: "passport",
      title: "Описание проекта",
      excerpt: projectPassport.shortDescription,
      meta: "Паспорт проекта",
      to: "/passport#description",
      searchableText: `${projectPassport.shortDescription} ${projectPassport.fullDescription}`,
    },
    {
      id: "passport-mission",
      group: "passport",
      title: "Миссия и принцип",
      excerpt: projectPassport.mission,
      meta: "Паспорт проекта",
      to: "/passport#mission",
      searchableText: `${projectPassport.mission} ${projectPassport.principle} ${projectPassport.supportingPrinciple}`,
    },
    ...projectPassport.directions.map((item, index) => ({
      id: `passport-direction-${index}`,
      group: "passport" as const,
      title: item.title,
      excerpt: item.description,
      meta: "Основные направления",
      to: "/passport#directions",
      searchableText: `${item.title} ${item.description}`,
    })),
    ...projectPassport.audiences.map((item, index) => ({
      id: `passport-audience-${index}`,
      group: "passport" as const,
      title: item.title,
      excerpt: item.description,
      meta: "Целевая аудитория",
      to: "/passport#audience",
      searchableText: `${item.title} ${item.description}`,
    })),
  ];
}

export const searchIndex: SearchResult[] = [
  ...flattenPassport(),
  ...verifiedFacts.map((fact) => ({
    id: fact.id,
    group: "facts" as const,
    title: fact.title,
    excerpt: fact.value,
    meta: `${fact.category} · ${fact.sourceTitle}`,
    to: `/facts/${fact.id}`,
    searchableText: [
      fact.title,
      fact.value,
      fact.shortDescription,
      fact.fullDescription,
      fact.category,
      fact.sourceTitle,
      fact.sourceType,
      fact.sourceNote,
      ...fact.tags,
    ].filter(Boolean).join(" "),
  })),
  ...projectWordings.map((wording) => ({
    id: wording.id,
    group: "wording" as const,
    title: wording.title,
    excerpt: wording.text,
    meta: `${wording.audience} · ${wording.language.toUpperCase()}`,
    to: `/wording/${wording.id}`,
    searchableText: [
      wording.title,
      wording.text,
      wording.format,
      wording.language,
      wording.audience,
      wording.usageNote,
    ].filter(Boolean).join(" "),
  })),
  ...brandAssets.map((asset) => ({
    id: asset.id,
    group: "assets" as const,
    title: asset.title,
    excerpt: asset.description ?? asset.usage ?? "",
    meta: `${asset.category} · ${asset.fileType.toUpperCase()}`,
    to: `/assets/${asset.id}`,
    searchableText: [
      asset.title,
      asset.description,
      asset.category,
      asset.fileType,
      asset.version,
      asset.usage,
      asset.language,
      ...asset.tags,
    ].filter(Boolean).join(" "),
  })),
];

export function normalizeSearch(value: string): string {
  return value
    .toLocaleLowerCase("ru-RU")
    .replace(/ё/g, "е")
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export function searchAll(query: string): SearchResult[] {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return [];

  const terms = normalizedQuery.split(" ").filter(Boolean);
  return searchIndex
    .map((item) => {
      const haystack = normalizeSearch(`${item.title} ${item.searchableText}`);
      const matches = terms.filter((term) => haystack.includes(term)).length;
      const titleBoost = normalizeSearch(item.title).includes(normalizedQuery) ? 3 : 0;
      return { item, score: matches + titleBoost };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}
